/**
 * <gwp-picker> — dual-style free-gift slider used in the cart drawer.
 *
 * Defined globally (not inline in the cart-drawer section) so the element is
 * always registered on page load. The cart drawer re-renders its gift region
 * via AJAX innerHTML, and inline <script> tags do not execute when inserted
 * that way — so any injected <gwp-picker> would never upgrade. Registering here
 * ensures injected pickers always upgrade and become interactive.
 */
(function () {
  if (customElements.get('gwp-picker')) return;

  customElements.define(
    'gwp-picker',
    class extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('.gwp-picker__rail');
        this.panels = Array.prototype.slice.call(this.querySelectorAll('.gwp-picker__panel'));
        this.dots = Array.prototype.slice.call(this.querySelectorAll('.gwp-picker__dot'));
        this.arrows = Array.prototype.slice.call(this.querySelectorAll('.gwp-picker__arrow'));
        this.index = 0;
        var self = this;

        this.dots.forEach(function (dot, i) {
          dot.addEventListener('click', function () { self.goTo(i); });
        });
        this.arrows.forEach(function (arrow) {
          arrow.addEventListener('click', function () {
            self.goTo(self.index + (parseInt(arrow.dataset.dir, 10) || 0));
          });
        });

        // Swipe: touch events for touch devices, pointer for mouse. Direction is
        // decided from start/end positions so it works even on a quick flick
        // where touchmove never fires.
        this._x0 = null;
        this._y0 = null;
        function onStart(x, y) { self._x0 = x; self._y0 = y; }
        function onEnd(x, y) {
          if (self._x0 === null) return;
          var dx = x - self._x0, dy = y - self._y0;
          self._x0 = self._y0 = null;
          if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) {
            self.goTo(self.index + (dx < 0 ? 1 : -1));
          }
        }
        this.addEventListener('touchstart', function (e) {
          var t = e.touches[0]; onStart(t.clientX, t.clientY);
        }, { passive: true });
        this.addEventListener('touchend', function (e) {
          var t = e.changedTouches[0];
          if (t) onEnd(t.clientX, t.clientY);
        });
        this.addEventListener('touchcancel', function () {
          self._x0 = self._y0 = null;
        });
        this.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'touch') return;
          onStart(e.clientX, e.clientY);
        });
        this.addEventListener('pointerup', function (e) {
          if (e.pointerType === 'touch') return;
          onEnd(e.clientX, e.clientY);
        });

        // Lift clipping only while an option dropdown is open, so it isn't cut
        // off by the slider's overflow.
        this._mo = new MutationObserver(function () {
          var open = !!self.querySelector('.disclosure--toggle[aria-expanded="true"]');
          self.classList.toggle('is-menu-open', open);
        });
        this._mo.observe(this, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });

        this._onResize = function () { self.update(); };
        window.addEventListener('resize', this._onResize);
        window.addEventListener('theme:drawer:right:opened', this._onResize);

        this.update();
      }

      disconnectedCallback() {
        if (this._mo) this._mo.disconnect();
        if (this._onResize) {
          window.removeEventListener('resize', this._onResize);
          window.removeEventListener('theme:drawer:right:opened', this._onResize);
        }
      }

      goTo(i) {
        this.index = Math.max(0, Math.min(this.panels.length - 1, i));
        this.update();
      }

      update() {
        var panel = this.panels[this.index];
        if (panel && this.rail) {
          // Offset with `left` (rail is position:relative) instead of transform
          // or margin-left: transform creates a WebKit clip/stacking context
          // that hides open dropdowns, and a negative margin-left would widen the
          // auto-width rail and stretch the panels. `left` shifts visually only.
          // Measure with cumulative offsetWidth (offsetLeft can be unreliable in
          // a flex rail on Safari).
          var styles = getComputedStyle(this.rail);
          var gap = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;
          var x = 0;
          for (var i = 0; i < this.index; i++) { x += this.panels[i].offsetWidth + gap; }
          this.rail.style.left = x ? ('-' + x + 'px') : '0px';
        }
        var target = panel ? panel.dataset.panel : null;
        this.dataset.active = target;
        this.dots.forEach(function (dot) {
          var on = dot.dataset.target === target;
          dot.classList.toggle('is-active', on);
          dot.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        var idx = this.index, last = this.panels.length - 1;
        this.arrows.forEach(function (arrow) {
          var dir = parseInt(arrow.dataset.dir, 10) || 0;
          var atEnd = (dir < 0 && idx <= 0) || (dir > 0 && idx >= last);
          arrow.disabled = atEnd;
          arrow.classList.toggle('is-disabled', atEnd);
        });
      }
    }
  );
})();
